import { AlertTriangle, ArrowUpCircle } from 'lucide-react';
import { pctOfQuota } from '../lib/useQuotaUsage';

/**
 * Banner de cupo para un módulo. Se muestra sólo si el uso está sobre 80%.
 * Prop `usage` es el objeto devuelto por `/api/plan/usage`.
 * `quotaKey`: 'rats' | 'users' | 'dpia_year' | 'breaches_year' | 'copilot_msgs_month'.
 * `onRequestUpgrade`: callback para abrir el modal de solicitud.
 */
export default function QuotaBanner({ usage, quotaKey, onRequestUpgrade }) {
  if (!usage) return null;
  const row = usage.quotas?.[quotaKey];
  if (!row || row.max === null || row.max === undefined) return null;

  const pct = pctOfQuota(row);
  if (pct < 80) return null;

  const atLimit = row.current >= row.max;
  const planName = usage.plan?.name || '';

  const tone = atLimit
    ? { bg: '#FEF2F2', border: '#FCA5A5', text: '#991B1B', icon: '#B91C1C' }
    : { bg: '#FFFBEB', border: '#FBBF24', text: '#78350F', icon: '#B45309' };

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 border rounded-md mb-4"
      style={{ background: tone.bg, borderColor: tone.border, color: tone.text }}
      data-testid={`quota-banner-${quotaKey}`}
    >
      <AlertTriangle size={16} className="mt-0.5 shrink-0" color={tone.icon} strokeWidth={2.2} />
      <div className="flex-1 text-[13px] leading-snug">
        <div className="font-semibold" data-testid={`quota-banner-${quotaKey}-title`}>
          {atLimit
            ? `Alcanzó el tope de ${row.label} del plan ${planName}.`
            : `Está al ${pct}% del tope de ${row.label} de su plan ${planName}.`}
        </div>
        <div className="mt-0.5 text-[12.5px] opacity-90 tabular-nums" data-testid={`quota-banner-${quotaKey}-count`}>
          Uso actual: <b>{row.current}</b> de <b>{row.max}</b>.
          {atLimit
            ? ' Para crear nuevos registros necesita aumentar su plan.'
            : ' Le recomendamos escalar antes de tocar el tope.'}
        </div>
      </div>
      {onRequestUpgrade && (
        <button
          type="button"
          onClick={onRequestUpgrade}
          className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md border transition-colors"
          style={{ borderColor: tone.icon, color: tone.icon, background: '#fff' }}
          data-testid={`quota-banner-${quotaKey}-upgrade`}
        >
          <ArrowUpCircle size={13} strokeWidth={2.2} />
          Solicitar aumento
        </button>
      )}
    </div>
  );
}
